import classNames from 'classnames';
import styles from './VehicleDefinition.module.css';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Dropdown,
  MenuProps,
  Space,
  List,
  Checkbox,
  CheckboxProps,
  Spin,
  InputRef,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import Modal from '../../Modal';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';
import Icon from 'src/components/Icon/Icon';

interface FormData {
  [key: string]: any;
}

interface VehicleDefinitionProps {
  isActive: boolean;
  onNext: (newData: { [key: string]: unknown }) => void;
  onBack: () => void;
  propData: FormData;
  form: FormInstance;
  queryType: string;
}
interface SelectedValues {
  [key: string]: any;
}
interface Brand {
  id: string;
  brand: string;
}
interface Model {
  id: string;
  model: string;
  modelDescription: string;
}
interface ModelYear {
  id: string;
  modelYear: string;
}
interface Plant {
  id: string;
  plant: string;
}
interface Market {
  id: string;
  market: string;
}
interface Trim {
  id: string;
  trim: string;
}
interface BodyStyle {
  id: string;
  bodyStyle: string;
}
interface Driver {
  id: string;
  driver: string;
}
interface EnginePower {
  id: string;
  engine: string;
  enginePowertrainDescription: string;
}
interface Transmission {
  id: string;
  transmission: string;
}
interface Emission {
  id: string;
  emission: string;
}

const placeholderMapping: { [key: string]: { [key: string]: string } } = {
  brand: { searchParameter: 'brand', displayKey: 'brand' },
  model: { searchParameter: 'model', displayKey: 'model' },
  modelYear: { searchParameter: 'modelYear', displayKey: 'modelYear' },
  plant: { searchParameter: 'plant', displayKey: 'plant' },
  market: { searchParameter: 'market', displayKey: 'market' },
  trim: { searchParameter: 'trim', displayKey: 'trim' },
  bodyStyle: { searchParameter: 'bodyStyle', displayKey: 'bodyStyle' },
  driver: { searchParameter: 'driver', displayKey: 'driver' },
  engine: {
    searchParameter: 'engine',
    displayKey: 'enginePowertrainDescription',
  },
  transmission: { searchParameter: 'transmission', displayKey: 'transmission' },
  emission: { searchParameter: 'emission', displayKey: 'emission' },
};

const VehicleDefinition = forwardRef<any, VehicleDefinitionProps>(
  ({ isActive, onNext, onBack, propData, form, queryType }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<FormData[]>([]);
    const [displayKey, setKey] = useState<string>('');
    const [vinField, setVin] = useState<string>('');
    const [tmpdata, setTmpData] = useState<{ [key: string]: any }>({});
    const [searchValues, setSearchValues] = useState<string>('');
    const inputRef = useRef<InputRef>(null);
    const [checked, setChecked] = useState(false);
    const [selectedValues, setSelectedValues] = useState<SelectedValues>({
      bodyStyle: null,
      brand: null,
      driver: null,
      emission: null,
      engine: null,
      enginePowertrainDescription: null,
      market: null,
      model: null,
      modelYear: null,
      plant: null,
      transmission: null,
      trim: null,
      vin: null,
      dateRangeOverriden: false,
    });
    const onFinish = (values: any) => {};
    const [concerned, setConcerned] = useState(propData?.dateRangeOverriden || false);
    const checkBoxOnChange: CheckboxProps['onChange'] = (e) => {
      setConcerned(e.target.checked);
    };

    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValues({
          bodyStyle: null,
          brand: null,
          driver: null,
          emission: null,
          engine: null,
          enginePowertrainDescription: null,
          market: null,
          model: null,
          modelYear: null,
          plant: null,
          transmission: null,
          trim: null,
          vin: null,
        });
      },
    }));

    useEffect(() => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        dateFrom: (propData?.dateFrom as string) || null,
        dateTo: (propData?.dateTo as string) || null,
        sourceSoftwareVersion: (propData?.sourceSoftwareVersion as string) || null,
        sourceType: (propData?.sourceType as string) || null,
      }));
      if (propData?.vin && Array.isArray(propData.vin)) {
        const vin = propData.vin.join(', ');
        setVin(vin);
        setSelectedValues((prev) => ({ ...prev, vin: propData.vin }));
      }
      setConcerned(propData?.dateRangeOverriden || false);
    }, [propData]);

    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      setOpenModal(true);
      setSearchValues('');
      form.setFieldValue('searchValues', '');
      if (placeholder === 'engine') {
        setTmpData({
          engine: selectedValues.engine as string,
          enginePowertrainDescription: selectedValues.enginePowertrainDescription as string,
        });
      } else {
        setTmpData({ [placeholder]: selectedValues[placeholder] as string });
      }

      setKey(placeholderMapping[placeholder].displayKey);
      switch (placeholder) {
        case 'brand':
          try {
            const searchParameter = 'brand';
            setIsLoading(true);
            const brand: Brand[] = await getSearchResultFromQueryParameter<Brand>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(brand);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'model':
          try {
            const searchParameter = 'model';
            setIsLoading(true);
            const model: Model[] = await getSearchResultFromQueryParameter<Model>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(model);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'modelYear':
          try {
            const searchParameter = 'modelYear';
            setIsLoading(true);
            const modelYear: ModelYear[] = await getSearchResultFromQueryParameter<ModelYear>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(modelYear);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'plant':
          try {
            const searchParameter = 'plant';
            setIsLoading(true);
            const plant: Plant[] = await getSearchResultFromQueryParameter<Plant>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(plant);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'market':
          try {
            const searchParameter = 'market';
            setIsLoading(true);
            const market: Market[] = await getSearchResultFromQueryParameter<Market>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(market);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'trim':
          try {
            const searchParameter = 'trim';
            setIsLoading(true);
            const trim: Trim[] = await getSearchResultFromQueryParameter<Trim>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(trim);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'bodyStyle':
          try {
            const searchParameter = 'bodyStyle';
            setIsLoading(true);
            const bodyStyle: BodyStyle[] = await getSearchResultFromQueryParameter<BodyStyle>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(bodyStyle);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'driver':
          try {
            const searchParameter = 'driver';
            setIsLoading(true);
            const driver: Driver[] = await getSearchResultFromQueryParameter<Driver>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(driver);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'engine':
          try {
            const searchParameter = 'engine';
            setIsLoading(true);
            const enginePowertrainDescription: EnginePower[] =
              await getSearchResultFromQueryParameter<EnginePower>(
                queryType,
                searchParameter,
                selectedValues
              );
            setResult(enginePowertrainDescription);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'transmission':
          try {
            const searchParameter = 'transmission';
            setIsLoading(true);
            const transmission: Transmission[] =
              await getSearchResultFromQueryParameter<Transmission>(
                queryType,
                searchParameter,
                selectedValues
              );
            setResult(transmission);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
          break;
        case 'emission':
          try {
            const searchParameter = 'emission';
            setIsLoading(true);
            const emission: Emission[] = await getSearchResultFromQueryParameter<Emission>(
              queryType,
              searchParameter,
              selectedValues
            );
            setResult(emission);
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
          }
          break;
        default:
          break;
      }
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      if (searchValues.length <= 0) {
        setIsInputFocused(false);
        setIsSmallText(false);
      }
    };

    const filteredResult = result.filter((item) =>
      item[displayKey]?.toLowerCase().includes(searchValues?.toLowerCase())
    );
    const onLinkClick = (type: string, value: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.includes(value)
          ? prevValues[type]
          : [...(prevValues[type] || []), value],
      }));
    };
    const handleRemove = (type: string, itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type]?.filter((item: string) => item !== itemRemove),
      }));
    };
    const engineLinkClick = (id: string, engineDescription: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        engine: prevValues.engine?.includes(id)
          ? prevValues.engine
          : [...(prevValues.engine || []), id],
        enginePowertrainDescription: prevValues.enginePowertrainDescription?.includes(
          engineDescription
        )
          ? prevValues.enginePowertrainDescription
          : [...(prevValues.enginePowertrainDescription || []), engineDescription],
      }));
    };
    const removeEngineLink = (item1: string, item2: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        engine: prevValues.engine.filter((item: string) => item !== item1),
        enginePowertrainDescription: prevValues.enginePowertrainDescription.filter(
          (item: string) => item !== item2
        ),
      }));
    };
    const handleApply = () => {
      setOpenModal(false);
      setTmpData({});
      if (inputPlaceholder === 'engine') {
        form.setFieldValue('engine', selectedValues.engine.join(', '));
      } else {
        form.setFieldValue(inputPlaceholder, selectedValues[inputPlaceholder].join(', '));
      }
    };
    const handleCancel = () => {
      const type = inputPlaceholder;
      if (type === 'engine') {
        setSelectedValues((prevValues) => ({
          ...prevValues,
          engine: [...(tmpdata.engine || [])],
          enginePowertrainDescription: [...(tmpdata.enginePowertrainDescription || [])],
        }));
      } else {
        setSelectedValues((prevValues) => ({
          ...prevValues,
          [type]: [...(tmpdata[type] || [])],
        }));
      }
      setOpenModal(false);
      setTmpData({});
    };

    const inputFields = [
      'bodyStyle',
      'brand',
      'driver',
      'emission',
      'engine',
      'enginePowertrainDescription',
      'market',
      'model',
      'modelYear',
      'plant',
      'transmission',
      'trim',
      'vin',
    ];

    const shouldShowResetButton =
      inputFields.some((key) => {
        const value = selectedValues[key];
        return Array.isArray(value) ? value.length > 0 : value !== null;
      }) || vinField.length > 0;

    const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const vinList = raw
        .split(/[\s,]+/)
        .map((vin) => vin.trim())
        .filter((vin) => vin !== '')
        .join(', '); // Reconstruct with commas
      form.setFieldValue('vin', vinList);
      setVin(vinList); // Update state if you're using useState for vinField
    };

    const handleNext = () => {
      const data = { ...selectedValues };

      // Normalize VINs
      const vinArray = vinField
        .split(/[\s,]+/)
        .map((n) => n.trim())
        .filter((n) => n.length > 0);
      data.vin = vinArray.length === 0 ? null : vinArray;

      // Helper to convert [] or null to null, otherwise return value
      const normalize = (value: any) => {
        if (Array.isArray(value)) return value.length === 0 ? null : value;
        return value;
      };

      // Normalize all input fields
      inputFields.forEach((field) => {
        data[field] = normalize(data[field]);
      });

      // Explicitly normalize VIN array
      data.vin = vinArray.length === 0 ? null : vinArray;

      // Engine fields override: force null always
      data.engine = normalize(selectedValues.engine);
      data.enginePowertrainDescription = normalize(selectedValues.enginePowertrainDescription);

      // Date override
      data.dateRangeOverriden = concerned;

      onNext(data);
    };

    const handleInputClear = (type: string) => {
      if (type === 'engine') {
        form.setFieldValue('engine', '');
        setSelectedValues((prevValues) => ({
          ...prevValues,
          enginePowertrainDescription: [],
          engine: [],
        }));
      } else {
        form.setFieldValue(type, '');
        setSelectedValues((prevValues) => ({
          ...prevValues,
          [type]: [],
        }));
      }
    };

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current?.focus();
    };

    const handleReset = () => {
      form.setFieldValue('brand', '');
      form.setFieldValue('model', '');
      form.setFieldValue('modelYear', '');
      form.setFieldValue('plant', '');
      form.setFieldValue('market', '');
      form.setFieldValue('trim', '');
      form.setFieldValue('bodyStyle', '');
      form.setFieldValue('driver', '');
      form.setFieldValue('engine', '');
      form.setFieldValue('transmission', '');
      form.setFieldValue('emission', '');
      form.setFieldValue('vin', '');
      setVin('');
      setSelectedValues({
        bodyStyle: null,
        brand: null,
        driver: null,
        emission: null,
        engine: null,
        enginePowertrainDescription: null,
        market: null,
        model: null,
        modelYear: null,
        plant: null,
        transmission: null,
        trim: null,
        vin: null,
      });
    };

    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '4',
        label: '50',
      },
      {
        key: '4',
        label: '70',
      },
    ];
    return (
      <div className={classNames(styles.VehicleDefinition)}>
        <div
          className={`
              ${styles['VehicleDefinition--QueryTitle']} 
              ${
                isActive
                  ? styles['VehicleDefinition--active']
                  : styles['VehicleDefinition--InActive']
              }
              `}
          // style={{ backgroundColor: isActive ? '#000000' : '#dcdcdc' }}
        >
          Vehicle Definition
          {shouldShowResetButton && (
            <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
              <Button
                className={`${
                  isActive
                    ? styles['VehicleDefinition--Reset']
                    : styles['VehicleDefinition--InActive']
                }`}
                type="primary"
                htmlType="submit"
                onClick={handleReset}
                disabled={!isActive}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        <div className={classNames(styles['VehicleDefinition--FormContainer'])}>
          <Form
            form={form}
            name="vehicle_definition_data_form"
            onFinish={onFinish}
            layout="horizontal"
            disabled={!isActive}
          >
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name="brand"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('brand')}
                    placeholder="Brand"
                    suffix={
                      selectedValues.brand?.length > 0 && (
                        <span onClick={() => handleInputClear('brand')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.brand?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Brand ({selectedValues.brand?.length})
                  </span>
                )}
              </Col>
              <Col span={4}>
                <Form.Item
                  name="model"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('model')}
                    placeholder="Model"
                    suffix={
                      selectedValues.model?.length > 0 && (
                        <span onClick={() => handleInputClear('model')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.model?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Model ({selectedValues.model?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="modelYear"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('modelYear')}
                    placeholder="Model Year"
                    suffix={
                      selectedValues.modelYear?.length > 0 && (
                        <span onClick={() => handleInputClear('modelYear')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.modelYear?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Model Year ({selectedValues.modelYear?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="plant"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('plant')}
                    placeholder="Plant"
                    suffix={
                      selectedValues.plant?.length > 0 && (
                        <span onClick={() => handleInputClear('plant')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.plant?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Plant ({selectedValues.plant?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="market"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('market')}
                    placeholder="Market"
                    suffix={
                      selectedValues.market?.length > 0 && (
                        <span onClick={() => handleInputClear('market')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.market?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Plant ({selectedValues.market?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="trim"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('trim')}
                    placeholder="Trim"
                    suffix={
                      selectedValues.trim?.length > 0 && (
                        <span onClick={() => handleInputClear('trim')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.trim?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Trim ({selectedValues.trim?.length})
                  </span>
                )}
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name="bodyStyle"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('bodyStyle')}
                    placeholder="Body Style"
                    suffix={
                      selectedValues.bodyStyle?.length > 0 && (
                        <span onClick={() => handleInputClear('bodyStyle')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.bodyStyle?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Body Style ({selectedValues.bodyStyle?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="driver"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('driver')}
                    placeholder="Driver"
                    suffix={
                      selectedValues.driver?.length > 0 && (
                        <span onClick={() => handleInputClear('driver')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.driver?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Driver ({selectedValues.driver?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="engine"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('engine')}
                    placeholder="Engine"
                    suffix={
                      selectedValues.engine?.length > 0 && (
                        <span onClick={() => handleInputClear('engine')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.engine?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Engine ({selectedValues.enginePowertrainDescription?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="transmission"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('transmission')}
                    placeholder="Transmission"
                    suffix={
                      selectedValues.transmission?.length > 0 && (
                        <span onClick={() => handleInputClear('transmission')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.transmission?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Transmission ({selectedValues.transmission?.length})
                  </span>
                )}
              </Col>

              <Col span={4}>
                <Form.Item
                  name="emission"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('emission')}
                    placeholder="Emission"
                    suffix={
                      selectedValues.emission?.length > 0 && (
                        <span onClick={() => handleInputClear('emission')}>
                          <Icon name="close" className={classNames(styles.clearIcon)} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.emission?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                  >
                    Emission ({selectedValues.emission?.length})
                  </span>
                )}
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="vin"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input.TextArea
                    placeholder="VIN"
                    onChange={(e) => setVin(e.target.value)} // Just store user input for now
                    onBlur={() => {
                      const vinArray = vinField
                        .split(/[\s,]+/) // Split on any combination of whitespace or commas
                        .map((v) => v.trim())
                        .filter((v) => v.length > 0); // Remove empty values
                      const formatted = vinArray.join(', ');
                      setVin(formatted);
                      form.setFieldValue('vin', formatted);
                    }}
                  />
                </Form.Item>
                {vinField?.length > 0 && (
                  <span
                    className={`${classNames(styles['VehicleDefinition--Placeholder'])} ${
                      styles.small
                    }`}
                    style={{ width: '99%' }}
                  >
                    VIN
                    <span
                      style={{ float: 'right' }}
                      onClick={() => {
                        form.setFieldValue('vin', '');
                        setVin('');
                      }}
                    >
                      <Icon name="close" className={classNames(styles.clearIcon)} />
                    </span>
                  </span>
                )}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="concerned" valuePropName="checked">
                  <Checkbox onChange={checkBoxOnChange} value={concerned}>
                    If entering VINs you can select all available data for those VINs by overriding
                    the date range filter. Check box to override and retrieve all data
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            {isActive && (
              <Row justify="space-between">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['VehicleDefinition--Buttons'])}
                      type="primary"
                      onClick={onBack}
                    >
                      Back
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['VehicleDefinition--Buttons'])}
                      type="primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Modal open={openModal} onClose={handleCancel} width={900} footer={null}>
              <div className={classNames(styles['VehicleDefinition--ModalHeader'])}>
                Select {inputPlaceholder}
              </div>
              <div className={classNames(styles['VehicleDefinition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['VehicleDefinition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['VehicleDefinition--TagsContainer'])}>
                    {inputPlaceholder === 'engine'
                      ? (selectedValues.engine || []).map((item: string, index: number) => {
                          return (
                            <Button
                              type="primary"
                              key={`${item}${index}`}
                              onClick={() =>
                                removeEngineLink(
                                  item,
                                  selectedValues.enginePowertrainDescription[index]
                                )
                              }
                              className={styles['selected-button']}
                            >
                              {item}
                              <Icon
                                name="close"
                                className={`${styles['VehicleDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                              />
                            </Button>
                          );
                        })
                      : (selectedValues[inputPlaceholder] || []).map(
                          (item: string, index: number) => {
                            return (
                              <Button
                                type="primary"
                                key={`${item}${index}`}
                                onClick={() => handleRemove(inputPlaceholder, item)}
                                className={styles['selected-button']}
                              >
                                {item}
                                <Icon
                                  name="close"
                                  className={`${styles['VehicleDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                                />
                              </Button>
                            );
                          }
                        )}
                  </div>
                </div>
                <div className={styles['VehicleDefinition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="searchFeilds"
                          value={searchValues}
                          onChange={(e) => setSearchValues(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['VehicleDefinition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={styles['VehicleDefinition--ModalDropDown']}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={styles['VehicleDefinition--ModalDropDownIcon']}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={styles['VehicleDefinition--SearchList']}>
                  {inputPlaceholder === 'engine' && (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredResult}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.engine?.includes(item.id)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => engineLinkClick(item.id, item.enginePowertrainDescription)}
                          style={{ display: 'flex', flexWrap: 'nowrap' }}
                        >
                          <div style={{ flex: 2, paddingRight: '10px' }}>
                            <span>{item.enginePowertrainDescription}</span>
                          </div>
                          <div style={{ flex: 3, paddingRight: '10px', whiteSpace: 'nowrap' }}>
                            <span>{item.engine}</span>
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                  {inputPlaceholder === 'model' ? (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredResult}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues[inputPlaceholder]?.includes(item.model)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick(inputPlaceholder, item.model as string)}
                        >
                          <span>{item.model}</span>
                          <span>{item.modelDescription}</span>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <List
                      itemLayout="horizontal"
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredResult}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues[inputPlaceholder]?.includes(item[displayKey])
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick(inputPlaceholder, item[displayKey] as string)}
                        >
                          <span>{item[displayKey]}</span>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className={styles['VehicleDefinition--ModalFooter']}>
                <Space>
                  <Button
                    onClick={handleCancel}
                    className={styles['VehicleDefinition--CancelButton']}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleApply} className={styles['VehicleDefinition--Buttons']}>
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
VehicleDefinition.displayName = 'VehicleDefinition';
export default VehicleDefinition;
